import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import FilmCard from '@/components/base/FilmCard';
import EmptyState from '@/components/base/EmptyState';
import MultiSelect from '@/components/base/MultiSelect';
import CategorySidebar from './components/CategorySidebar';
import { categories, regions } from '@/mocks/categories';
import { films } from '@/mocks/films';
import { sortFilms, yearOptions, type SortKey } from '@/utils/content';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 8;

const sortTabs: { key: SortKey; label: string; icon: string }[] = [
  { key: 'latest', label: '最新', icon: 'ri-time-line' },
  { key: 'popular', label: '热门', icon: 'ri-fire-line' },
  { key: 'rating', label: '评分', icon: 'ri-star-line' },
];

export default function CategoryPage() {
  const { categoryId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSub = searchParams.get('sub') ?? '';

  const category = categories.find((item) => item.id === categoryId);

  const [regionFilter, setRegionFilter] = useState<string[]>([]);
  const [yearFilter, setYearFilter] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>('latest');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  usePageMeta({
    title: `成人影视 - ${category ? category.name : '分类'}`,
    description: category
      ? `${category.description} 浏览成人影视「${category.name}」分类下的内容与推荐，支持按地区、年份筛选与排序。`
      : '浏览成人影视各分类下的内容与推荐，支持按地区、年份筛选与排序。',
    keywords: `成人影视,${category ? category.name : '影视分类'},影视资讯,分类浏览`,
  });

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [categoryId, activeSub, regionFilter, yearFilter, sort]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  const filtered = useMemo(() => {
    const base = films.filter((film) => film.category === categoryId);
    const bySub = activeSub ? base.filter((film) => film.subcategory === activeSub) : base;
    const byRegion = regionFilter.length ? bySub.filter((film) => regionFilter.includes(film.region)) : bySub;
    const byYear = yearFilter.length ? byRegion.filter((film) => yearFilter.includes(String(film.year))) : byRegion;
    return sortFilms(byYear, sort);
  }, [categoryId, activeSub, regionFilter, yearFilter, sort]);

  const resetAll = () => {
    setRegionFilter([]);
    setYearFilter([]);
    setSort('latest');
    setSearchParams({});
  };

  const handleSubChange = (sub: string) => {
    if (sub) {
      setSearchParams({ sub });
    } else {
      setSearchParams({});
    }
  };

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="page-shell py-20">
          <EmptyState
            icon="ri-folder-unknow-line"
            title="分类不存在"
            description="你访问的分类可能已被移除或链接有误，可以返回首页重新浏览现有分类。"
            primaryAction={{ label: '返回首页', to: '/' }}
            secondaryAction={{ label: '查看全部内容', to: '/category/drama' }}
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const shown = filtered.slice(0, visible);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main>
        <section className="border-b border-background-300/70 bg-background-100">
          <div className="page-shell py-8 md:py-10">
            <nav className="mb-5 flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-400">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span>分类</span>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800">{category.name}</span>
              {activeSub && (
                <>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-primary-400">{activeSub}</span>
                </>
              )}
            </nav>
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <p className="eyebrow mb-2">Category</p>
                <h1 className="text-2xl font-semibold text-foreground-950 md:text-[32px]">{category.name}</h1>
                <p className="mt-3 text-[13px] leading-7 text-foreground-600">{category.description}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-[12px] text-foreground-600">
                <i className="ri-folder-line text-primary-400" />
                共 <span className="text-foreground-900">{filtered.length}</span> 部条目
              </div>
            </div>
          </div>
        </section>

        <section className="page-shell pt-8">
          <div className="grid gap-8 lg:grid-cols-12">
            <aside className="hidden lg:col-span-3 lg:block">
              <div className="sticky top-24">
                <CategorySidebar activeId={category.id} activeSub={activeSub} />
                <div className="mt-4 rounded-lg border border-background-300/70 bg-background-100 p-4">
                  <p className="mb-2 flex items-center gap-1.5 text-[12px] text-foreground-800">
                    <i className="ri-information-line text-primary-400" />
                    关于本分类
                  </p>
                  <p className="text-[11px] leading-6 text-foreground-500">
                    子分类可在上方展开选择。筛选条件会实时作用于右侧列表，排序不影响筛选结果。
                  </p>
                </div>
              </div>
            </aside>

            <div className="lg:col-span-9">
              <div className="rounded-lg border border-background-300/70 bg-background-100 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(true)}
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-[12px] text-foreground-700 lg:hidden"
                  >
                    <i className="ri-filter-3-line" />
                    分类与筛选
                  </button>
                  <MultiSelect label="地区" options={regions} selected={regionFilter} onChange={setRegionFilter} />
                  <MultiSelect label="年份" options={yearOptions} selected={yearFilter} onChange={setYearFilter} />
                  {(regionFilter.length > 0 || yearFilter.length > 0) && (
                    <button
                      type="button"
                      onClick={resetAll}
                      className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-2 text-[12px] text-foreground-500 transition-colors hover:text-primary-400"
                    >
                      <i className="ri-close-line" />
                      清除筛选
                    </button>
                  )}

                  <div className="ml-auto flex items-center gap-0.5 rounded-full border border-background-300 bg-background-50 p-1">
                    {sortTabs.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setSort(tab.key)}
                        className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-[12px] transition-colors ${
                          sort === tab.key
                            ? 'bg-primary-500 text-background-50'
                            : 'text-foreground-600 hover:text-foreground-950'
                        }`}
                      >
                        <i className={`${tab.icon} text-[13px]`} />
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-background-300/70 pt-3">
                  <span className="mr-1 font-label text-[10px] tracking-wider text-foreground-500">类型</span>
                  <button
                    type="button"
                    onClick={() => handleSubChange('')}
                    className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1.5 text-[11px] transition-colors ${
                      !activeSub
                        ? 'bg-secondary-500/20 text-secondary-700'
                        : 'text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    全部
                  </button>
                  {category.children.map((child) => (
                    <button
                      key={child.id}
                      type="button"
                      onClick={() => handleSubChange(child.name)}
                      className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1.5 text-[11px] transition-colors ${
                        activeSub === child.name
                          ? 'bg-secondary-500/20 text-secondary-700'
                          : 'text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {child.name}
                    </button>
                  ))}
                </div>
              </div>

              {shown.length > 0 ? (
                <>
                  <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 xl:grid-cols-4">
                    {shown.map((film, index) => (
                      <FilmCard key={film.id} film={film} index={index} />
                    ))}
                  </div>

                  <div className="mt-10 flex flex-col items-center gap-3">
                    <p className="text-[11px] text-foreground-500">
                      已显示 {shown.length} / {filtered.length} 部条目
                    </p>
                    {shown.length < filtered.length ? (
                      <button
                        type="button"
                        onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                        className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-6 py-3 text-[13px] text-foreground-800 transition-colors hover:border-primary-500/60 hover:text-primary-400"
                      >
                        <i className="ri-add-line" />
                        加载更多内容
                      </button>
                    ) : (
                      <span className="rounded-md border border-background-300/70 px-6 py-3 text-[12px] text-foreground-500">
                        已显示该分类下全部条目
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <div className="mt-6">
                  <EmptyState
                    icon="ri-file-search-line"
                    title="没有符合条件的内容"
                    description="当前筛选组合下没有收录条目。可以清除筛选条件，或返回首页浏览其他分类。"
                    primaryAction={{ label: '重置筛选条件', onClick: resetAll }}
                    secondaryAction={{ label: '返回首页', to: '/' }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-background-950/70 animate-soft-fade"
            onClick={() => setDrawerOpen(false)}
            role="presentation"
          />
          <div className="absolute inset-y-0 left-0 flex w-[86%] max-w-sm flex-col overflow-y-auto border-r border-background-300 bg-background-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-heading text-base text-foreground-950">分类与筛选</p>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700"
                aria-label="关闭筛选"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <MultiSelect label="按地区筛选" options={regions} selected={regionFilter} onChange={setRegionFilter} />
            <div className="mt-2">
              <MultiSelect label="按年份筛选" options={yearOptions} selected={yearFilter} onChange={setYearFilter} />
            </div>
            <div className="mt-4">
              <CategorySidebar activeId={category.id} activeSub={activeSub} />
            </div>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={resetAll}
                className="flex-1 cursor-pointer rounded-md border border-background-300 py-2.5 text-[12px] text-foreground-700"
              >
                清除筛选
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex-1 cursor-pointer rounded-md bg-primary-500 py-2.5 text-[12px] text-background-50"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}